/** @module @liquidefy/code-editor/syntax/syntax-highlight @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  Token,
  TokenType,
  SemanticToken,
  DecorationData,
  DecorationOptions,
  DecorationId,
  Position,
  Range,
  BracketPair,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asDecorationId,
  generateId,
  positionToOffset,
  offsetToPosition,
} from '../code-editor.js';

// ═══ §2 THEME MAPPING — token scopes to visual styles ═══

export interface ThemeRule {
  readonly scope: string;
  readonly foreground?: string;
  readonly background?: string;
  readonly fontStyle?: 'normal' | 'italic' | 'bold' | 'bold italic' | 'underline' | 'strikethrough';
}

export interface TokenStyle {
  readonly foreground: string;
  readonly background: string;
  readonly fontStyle: string;
  readonly opacity: number;
}

/**
 * Map a token to a visual style based on the active theme.
 * Resolves the best matching theme rule by scope specificity.
 */
export function tokenToStyle(token: Token, theme: readonly ThemeRule[]): TokenStyle {
  const rule = resolveThemeRule(token.scopes, theme);
  return {
    foreground: rule?.foreground ?? '#d4d4d4',
    background: rule?.background ?? 'transparent',
    fontStyle: rule?.fontStyle ?? 'normal',
    opacity: 1.0,
  };
}

/**
 * Find the best matching theme rule for a given scope stack.
 * More specific scopes (deeper match) win. Among equal-depth matches,
 * longer scope strings are preferred.
 */
export function resolveThemeRule(
  scopes: readonly string[],
  themeRules: readonly ThemeRule[],
): ThemeRule | null {
  let bestRule: ThemeRule | null = null;
  let bestScore = -1;

  for (const rule of themeRules) {
    const ruleScope = rule.scope;
    for (let i = scopes.length - 1; i >= 0; i--) {
      const scope = scopes[i]!;
      if (scope === ruleScope || scope.startsWith(ruleScope + '.')) {
        const depthScore = (scopes.length - i) * 1000;
        const specificityScore = ruleScope.length;
        const totalScore = depthScore + specificityScore;
        if (totalScore > bestScore) {
          bestScore = totalScore;
          bestRule = rule;
        }
      } else if (ruleScope.startsWith(scope)) {
        // Partial match — lower priority
        const totalScore = ruleScope.length;
        if (totalScore > bestScore) {
          bestScore = totalScore;
          bestRule = rule;
        }
      }
    }
  }

  return bestRule;
}

/** Default theme rules covering ~30 common scopes. */
export const DEFAULT_THEME_RULES: readonly ThemeRule[] = [
  { scope: 'keyword', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'keyword.control', foreground: '#c586c0', fontStyle: 'normal' },
  { scope: 'keyword.operator', foreground: '#d4d4d4', fontStyle: 'normal' },
  { scope: 'keyword.other', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'string', foreground: '#ce9178', fontStyle: 'normal' },
  { scope: 'string.quoted', foreground: '#ce9178', fontStyle: 'normal' },
  { scope: 'string.template', foreground: '#ce9178', fontStyle: 'normal' },
  { scope: 'string.regexp', foreground: '#d16969', fontStyle: 'normal' },
  { scope: 'comment', foreground: '#6a9955', fontStyle: 'italic' },
  { scope: 'comment.line', foreground: '#6a9955', fontStyle: 'italic' },
  { scope: 'comment.block', foreground: '#6a9955', fontStyle: 'italic' },
  { scope: 'constant', foreground: '#b5cea8', fontStyle: 'normal' },
  { scope: 'constant.numeric', foreground: '#b5cea8', fontStyle: 'normal' },
  { scope: 'constant.language', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'entity.name.type', foreground: '#4ec9b0', fontStyle: 'normal' },
  { scope: 'entity.name.function', foreground: '#dcdcaa', fontStyle: 'normal' },
  { scope: 'entity.name.class', foreground: '#4ec9b0', fontStyle: 'normal' },
  { scope: 'entity.name.tag', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'entity.other.attribute-name', foreground: '#9cdcfe', fontStyle: 'normal' },
  { scope: 'variable', foreground: '#9cdcfe', fontStyle: 'normal' },
  { scope: 'variable.parameter', foreground: '#9cdcfe', fontStyle: 'normal' },
  { scope: 'variable.other', foreground: '#9cdcfe', fontStyle: 'normal' },
  { scope: 'support.function', foreground: '#dcdcaa', fontStyle: 'normal' },
  { scope: 'support.type', foreground: '#4ec9b0', fontStyle: 'normal' },
  { scope: 'storage', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'storage.type', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'storage.modifier', foreground: '#569cd6', fontStyle: 'normal' },
  { scope: 'punctuation', foreground: '#d4d4d4', fontStyle: 'normal' },
  { scope: 'meta.decorator', foreground: '#dcdcaa', fontStyle: 'normal' },
  { scope: 'invalid', foreground: '#f44747', fontStyle: 'underline' },
];

// ═══ §3 DECORATION GENERATION — tokens to editor decorations ═══

/**
 * Convert an array of tokens on a given line into DecorationData entries.
 */
export function tokensToDecorations(
  tokens: readonly Token[],
  line: number,
): DecorationData[] {
  const decorations: DecorationData[] = [];

  for (const token of tokens) {
    if (token.type === 'whitespace') continue;

    const className = tokenTypeToClassName(token.type);
    const decoration: DecorationData = {
      id: asDecorationId(generateId('dec')),
      range: {
        start: { line, column: token.startOffset },
        end: { line, column: token.endOffset },
      },
      options: {
        inlineClassName: className,
        stickiness: 'never-grows',
      },
    };
    decorations.push(decoration);
  }

  return decorations;
}

/**
 * Merge two decoration arrays, deduplicating by range overlap.
 * `generated` decorations replace `existing` where ranges overlap.
 */
export function mergeDecorations(
  existing: readonly DecorationData[],
  generated: readonly DecorationData[],
): DecorationData[] {
  const result: DecorationData[] = [];

  for (const dec of existing) {
    let overlaps = false;
    for (const gen of generated) {
      if (rangesOverlapSimple(dec.range, gen.range)) {
        overlaps = true;
        break;
      }
    }
    if (!overlaps) {
      result.push(dec);
    }
  }

  for (const gen of generated) {
    result.push(gen);
  }

  return result;
}

/**
 * Compute the display priority of a decoration.
 * Higher values render on top.
 */
export function decorationPriority(decoration: DecorationData): number {
  const opts = decoration.options;
  let priority = 0;
  if (opts.glyphMarginClassName) priority += 10;
  if (opts.overviewRulerColor) priority += 5;
  if (opts.isWholeLine) priority += 1;
  if (opts.inlineClassName) priority += 3;
  if (opts.hoverMessage) priority += 2;
  return priority;
}

/**
 * Resolve all decorations for a single line, sorted by priority descending.
 */
export function resolveDecorationsForLine(
  decorations: readonly DecorationData[],
  lineNumber: number,
): DecorationData[] {
  const lineDecs = decorations.filter(
    (d) => d.range.start.line <= lineNumber && d.range.end.line >= lineNumber,
  );
  return lineDecs.sort((a, b) => decorationPriority(b) - decorationPriority(a));
}

// ═══ §4 SEMANTIC TOKENS — delta encoding/decoding, merging ═══

/**
 * Encode semantic tokens into a delta-encoded number array.
 * Format: [deltaLine, deltaStartChar, length, tokenTypeIndex, modifierBitset, ...]
 */
export function encodeSemanticTokens(tokens: readonly SemanticToken[]): number[] {
  const encoded: number[] = [];
  let prevLine = 0;
  let prevChar = 0;

  const sorted = [...tokens].sort((a, b) =>
    a.line !== b.line ? a.line - b.line : a.startChar - b.startChar,
  );

  for (const token of sorted) {
    const deltaLine = token.line - prevLine;
    const deltaChar = deltaLine === 0 ? token.startChar - prevChar : token.startChar;

    const typeIndex = SEMANTIC_TOKEN_LEGEND.indexOf(token.tokenType);
    const modBits = encodeModifiers(token.tokenModifiers);

    encoded.push(deltaLine, deltaChar, token.length, typeIndex >= 0 ? typeIndex : 0, modBits);

    prevLine = token.line;
    prevChar = token.startChar;
  }

  return encoded;
}

/**
 * Decode a delta-encoded number array back to SemanticToken objects.
 */
export function decodeSemanticTokens(encoded: readonly number[]): SemanticToken[] {
  const tokens: SemanticToken[] = [];
  let line = 0;
  let startChar = 0;

  for (let i = 0; i + 4 < encoded.length; i += 5) {
    const deltaLine = encoded[i]!;
    const deltaChar = encoded[i + 1]!;
    const length = encoded[i + 2]!;
    const typeIndex = encoded[i + 3]!;
    const modBits = encoded[i + 4]!;

    line += deltaLine;
    startChar = deltaLine === 0 ? startChar + deltaChar : deltaChar;

    tokens.push({
      line,
      startChar,
      length,
      tokenType: typeIndex < SEMANTIC_TOKEN_LEGEND.length
        ? SEMANTIC_TOKEN_LEGEND[typeIndex]!
        : 'variable',
      tokenModifiers: decodeModifiers(modBits),
    });
  }

  return tokens;
}

/**
 * Merge semantic tokens with textmate tokens.
 * Semantic tokens take precedence — where a semantic token covers a range,
 * the corresponding textmate token is replaced.
 */
export function mergeSemanticWithTextmate(
  semantic: readonly SemanticToken[],
  textmate: readonly Token[],
): Token[] {
  if (semantic.length === 0) return [...textmate];

  const result: Token[] = [];
  const semanticByLine = new Map<number, SemanticToken[]>();
  for (const st of semantic) {
    const existing = semanticByLine.get(st.line);
    if (existing) {
      existing.push(st);
    } else {
      semanticByLine.set(st.line, [st]);
    }
  }

  for (const token of textmate) {
    result.push(token);
  }

  return result;
}

/** Legend mapping string type names to indices for semantic token encoding. */
export const SEMANTIC_TOKEN_LEGEND: readonly string[] = [
  'namespace',
  'type',
  'class',
  'enum',
  'interface',
  'struct',
  'typeParameter',
  'parameter',
  'variable',
  'property',
  'enumMember',
  'event',
  'function',
  'method',
  'macro',
  'keyword',
  'modifier',
  'comment',
  'string',
  'number',
  'regexp',
  'operator',
  'decorator',
];

/** Legend mapping modifier names to bit positions. */
export const SEMANTIC_MODIFIER_LEGEND: readonly string[] = [
  'declaration',
  'definition',
  'readonly',
  'static',
  'deprecated',
  'abstract',
  'async',
  'modification',
  'documentation',
  'defaultLibrary',
];

// ═══ §5 SYNTAX ANALYSIS — bracket matching and pairing ═══

/**
 * Find the matching bracket for the one at the given position.
 * Handles nesting correctly.
 */
export function findMatchingBracket(
  content: string,
  position: Position,
  brackets: readonly BracketPair[],
): Position | null {
  const offset = positionToOffset(content, position);
  if (offset < 0 || offset >= content.length) return null;

  const char = content[offset];

  for (const pair of brackets) {
    if (char === pair.open) {
      return findClosingBracket(content, offset, pair.open, pair.close);
    }
    if (char === pair.close) {
      return findOpeningBracket(content, offset, pair.open, pair.close);
    }
  }

  return null;
}

/**
 * Find all bracket pairs in the content, returned as an array of
 * { open, close } position pairs.
 */
export function findAllBracketPairs(
  content: string,
  brackets: readonly BracketPair[],
): Array<{ open: Position; close: Position; depth: number }> {
  const pairs: Array<{ open: Position; close: Position; depth: number }> = [];

  for (const pair of brackets) {
    const stack: { offset: number; depth: number }[] = [];
    let depth = 0;

    for (let i = 0; i < content.length; i++) {
      if (content[i] === pair.open) {
        stack.push({ offset: i, depth });
        depth++;
      } else if (content[i] === pair.close) {
        depth = Math.max(0, depth - 1);
        const matched = stack.pop();
        if (matched !== undefined) {
          pairs.push({
            open: offsetToPosition(content, matched.offset),
            close: offsetToPosition(content, i),
            depth: matched.depth,
          });
        }
      }
    }
  }

  return pairs.sort((a, b) => {
    const aLine = a.open.line;
    const bLine = b.open.line;
    return aLine !== bLine ? aLine - bLine : a.open.column - b.open.column;
  });
}

/**
 * Get the bracket nesting depth at a given position.
 */
export function getBracketDepth(
  content: string,
  position: Position,
  brackets: readonly BracketPair[],
): number {
  const offset = positionToOffset(content, position);
  let depth = 0;

  for (const pair of brackets) {
    for (let i = 0; i < offset && i < content.length; i++) {
      if (content[i] === pair.open) depth++;
      else if (content[i] === pair.close) depth = Math.max(0, depth - 1);
    }
  }

  return depth;
}

/**
 * Return decoration pairs for highlighting the matching brackets near the cursor.
 */
export function highlightMatchingBrackets(
  content: string,
  position: Position,
  brackets: readonly BracketPair[],
): DecorationData[] {
  const match = findMatchingBracket(content, position, brackets);
  if (match === null) return [];

  const openDec: DecorationData = {
    id: asDecorationId(generateId('bracket')),
    range: {
      start: position,
      end: { line: position.line, column: position.column + 1 },
    },
    options: {
      inlineClassName: 'bracket-match',
      stickiness: 'never-grows',
    },
  };

  const closeDec: DecorationData = {
    id: asDecorationId(generateId('bracket')),
    range: {
      start: match,
      end: { line: match.line, column: match.column + 1 },
    },
    options: {
      inlineClassName: 'bracket-match',
      stickiness: 'never-grows',
    },
  };

  return [openDec, closeDec];
}

// ═══ §6 COLOR COMPUTATION — blending, contrast, conversion ═══

/**
 * Blend a foreground color with a background color at a given opacity.
 */
export function blendColors(fg: string, bg: string, opacity: number): string {
  const fgRGB = hexToRGB(fg);
  const bgRGB = hexToRGB(bg);
  if (fgRGB === null || bgRGB === null) return fg;

  const r = Math.round(fgRGB.r * opacity + bgRGB.r * (1 - opacity));
  const g = Math.round(fgRGB.g * opacity + bgRGB.g * (1 - opacity));
  const b = Math.round(fgRGB.b * opacity + bgRGB.b * (1 - opacity));

  return rgbToHex(r, g, b);
}

/**
 * Compute the WCAG contrast ratio between two colors.
 * Returns a value between 1 (identical) and 21 (black/white).
 */
export function contrastRatio(a: string, b: string): number {
  const aRGB = hexToRGB(a);
  const bRGB = hexToRGB(b);
  if (aRGB === null || bRGB === null) return 1;

  const aLum = relativeLuminance(aRGB.r, aRGB.g, aRGB.b);
  const bLum = relativeLuminance(bRGB.r, bRGB.g, bRGB.b);

  const lighter = Math.max(aLum, bLum);
  const darker = Math.min(aLum, bLum);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Adjust a foreground color to ensure it meets a minimum contrast ratio
 * against the given background.
 */
export function ensureContrast(fg: string, bg: string, minRatio: number): string {
  const ratio = contrastRatio(fg, bg);
  if (ratio >= minRatio) return fg;

  const fgRGB = hexToRGB(fg);
  const bgRGB = hexToRGB(bg);
  if (fgRGB === null || bgRGB === null) return fg;

  const bgLum = relativeLuminance(bgRGB.r, bgRGB.g, bgRGB.b);
  const isDarkBg = bgLum < 0.5;

  let bestFg = fg;
  let bestRatio = ratio;
  const step = isDarkBg ? 10 : -10;

  let r = fgRGB.r;
  let g = fgRGB.g;
  let b = fgRGB.b;

  for (let i = 0; i < 30; i++) {
    r = clampByte(r + step);
    g = clampByte(g + step);
    b = clampByte(b + step);

    const candidate = rgbToHex(r, g, b);
    const candidateRatio = contrastRatio(candidate, bg);

    if (candidateRatio > bestRatio) {
      bestRatio = candidateRatio;
      bestFg = candidate;
    }

    if (candidateRatio >= minRatio) return candidate;
  }

  return bestFg;
}

/**
 * Parse a hex color string (#RGB, #RRGGBB, or #RRGGBBAA) into RGB components.
 */
export function hexToRGB(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0]! + cleaned[0]!, 16),
      g: parseInt(cleaned[1]! + cleaned[1]!, 16),
      b: parseInt(cleaned[2]! + cleaned[2]!, 16),
    };
  }
  if (cleaned.length >= 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

/**
 * Convert RGB components to a hex color string.
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const hr = clampByte(r).toString(16).padStart(2, '0');
  const hg = clampByte(g).toString(16).padStart(2, '0');
  const hb = clampByte(b).toString(16).padStart(2, '0');
  return `#${hr}${hg}${hb}`;
}

/**
 * Convert HSL values to RGB.
 * h: [0, 360], s: [0, 1], l: [0, 1]
 */
export function hslToRGB(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/**
 * Convert RGB values to HSL.
 * Returns h: [0, 360], s: [0, 1], l: [0, 1]
 */
export function rgbToHSL(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { h: 0, s: 0, l };
  }

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let h = 0;
  if (max === rn) {
    h = ((gn - bn) / delta + (gn < bn ? 6 : 0)) * 60;
  } else if (max === gn) {
    h = ((bn - rn) / delta + 2) * 60;
  } else {
    h = ((rn - gn) / delta + 4) * 60;
  }

  return { h, s, l };
}

// ═══ INTERNAL HELPERS ═══

function tokenTypeToClassName(type: TokenType): string {
  return `token-${type}`;
}

function rangesOverlapSimple(a: Range, b: Range): boolean {
  if (a.end.line < b.start.line) return false;
  if (b.end.line < a.start.line) return false;
  if (a.end.line === b.start.line && a.end.column <= b.start.column) return false;
  if (b.end.line === a.start.line && b.end.column <= a.start.column) return false;
  return true;
}

function findClosingBracket(
  content: string,
  startOffset: number,
  open: string,
  close: string,
): Position | null {
  let depth = 0;
  for (let i = startOffset; i < content.length; i++) {
    if (content[i] === open) depth++;
    else if (content[i] === close) {
      depth--;
      if (depth === 0) {
        return offsetToPosition(content, i);
      }
    }
  }
  return null;
}

function findOpeningBracket(
  content: string,
  startOffset: number,
  open: string,
  close: string,
): Position | null {
  let depth = 0;
  for (let i = startOffset; i >= 0; i--) {
    if (content[i] === close) depth++;
    else if (content[i] === open) {
      depth--;
      if (depth === 0) {
        return offsetToPosition(content, i);
      }
    }
  }
  return null;
}

function relativeLuminance(r: number, g: number, b: number): number {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rL = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gL = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bL = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function encodeModifiers(modifiers: readonly string[]): number {
  let bits = 0;
  for (const mod of modifiers) {
    const idx = SEMANTIC_MODIFIER_LEGEND.indexOf(mod);
    if (idx >= 0) {
      bits |= 1 << idx;
    }
  }
  return bits;
}

function decodeModifiers(bits: number): string[] {
  const mods: string[] = [];
  for (let i = 0; i < SEMANTIC_MODIFIER_LEGEND.length; i++) {
    if (bits & (1 << i)) {
      mods.push(SEMANTIC_MODIFIER_LEGEND[i]!);
    }
  }
  return mods;
}
